import { workflowRestartIntent, type WorkflowRestartIntent } from "./intent-model.ts";
import { useWorkflowQuery, useWorkflowMutation, useWorkflowCompletion, useWorkflowClipboard } from "./react.tsx";
import { workflowControlBusyKey } from "./mutation-model.ts";
import { Button, Dialog, DropdownMenu, Tooltip } from "@cloudflare/kumo";
import {
  ArrowClockwiseIcon,
  BellIcon,
  CaretDownIcon,
  CodeIcon,
  FlowArrow,
  ListBulletsIcon,
  MagnifyingGlassIcon,
  MoonIcon,
  PaperPlaneTiltIcon,
  PauseIcon,
  PlayIcon,
  StopIcon
} from "@phosphor-icons/react";
import { memo, useCallback, useEffect, useState } from "react";
import { IconCopyButton } from "./IconCopyButton";
import { ScrollableCodeBlock } from "../../ScrollableCodeBlock";
import {
  formatDuration,
  formatJson,
  getAvailableActions,
  isTerminalStatus,
  type WorkflowUiAction
} from "./workflow-format";
import { WorkflowSendEventDialog } from "./WorkflowSendEventDialog";
import {
  WorkflowStatusBadge,
  WorkflowStatusIcon
} from "./WorkflowStatusBadge";
import { getStepKey, WorkflowStepRow } from "./WorkflowStepRow";
import {
  type WorkflowDefinitionRecord,
  type WorkflowInstanceRecord,
  type WorkflowStepRecord
} from "../../api/workflows/model.ts";

// Adapted from workers-sdk's local-explorer-ui workflow instance detail
// view: stats strip, params/output panes, and searchable step history with
// restart-from-step. Instance deletion is omitted (no server support);
// step records here carry no per-attempt log.

interface WorkflowInstanceSurfaceProps {
  workspaceId: string;
  definition: WorkflowDefinitionRecord;
  instanceId: string;
  active: boolean;
  onOpenSource: (file: string, line: number) => void;
  onOpenGraph: () => void;
}

const ACTION_ICONS: Record<WorkflowUiAction, typeof PauseIcon> = {
  pause: PauseIcon,
  resume: PlayIcon,
  terminate: StopIcon,
  restart: ArrowClockwiseIcon
};

const StatsStrip = memo(function StatsStrip({
  status,
  stepCount,
  start,
  end
}: {
  status: string;
  stepCount: number;
  start?: number;
  end?: number;
}) {
  return (
    <div className="grid grid-cols-1 overflow-hidden rounded-lg border border-kumo-fill bg-kumo-base md:grid-cols-3">
      <div className="flex items-center justify-between px-3 py-2 md:px-5">
        <span className="text-sm text-kumo-subtle md:text-base">Status</span>
        <WorkflowStatusBadge status={status} />
      </div>
      <div className="flex items-center justify-between border-t border-kumo-fill px-3 py-2 md:border-t-0 md:border-l md:px-5">
        <span className="text-sm text-kumo-subtle md:text-base">
          Steps Recorded
        </span>
        <span className="text-sm font-medium text-kumo-default md:text-base">
          {stepCount}
        </span>
      </div>
      <div className="flex items-center justify-between border-t border-kumo-fill px-3 py-2 md:border-t-0 md:border-l md:px-5">
        <span className="text-sm text-kumo-subtle md:text-base">Duration</span>
        <span className="text-sm font-medium text-kumo-default md:text-base">
          {formatDuration(start, end)}
        </span>
      </div>
    </div>
  );
});

const ParamsOutput = memo(function ParamsOutput({
  params,
  output
}: {
  params: unknown;
  output: unknown;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-kumo-fill bg-kumo-tint">
      <div className="grid grid-cols-2 divide-x divide-kumo-fill">
        <div className="px-4 py-2.5">
          <span className="text-sm font-medium text-kumo-default">
            Input params
          </span>
        </div>
        <div className="px-4 py-2.5">
          <span className="text-sm font-medium text-kumo-default">Output</span>
        </div>
      </div>
      <div className="rounded-t-lg border-t border-kumo-fill bg-kumo-base">
        <div className="grid grid-cols-2 divide-x divide-kumo-fill">
          <div className="relative">
            <ScrollableCodeBlock content={formatJson(params)} />
            <div className="absolute top-1.5 right-1.5">
              <IconCopyButton text={formatJson(params)} label="Copy params" />
            </div>
          </div>
          <div className="relative">
            <ScrollableCodeBlock content={formatJson(output)} />
            <div className="absolute top-1.5 right-1.5">
              <IconCopyButton text={formatJson(output)} label="Copy output" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const ErrorCard = memo(function ErrorCard({
  error
}: {
  error: { name?: string; message?: string };
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-kumo-fill bg-kumo-tint">
      <div className="px-4 py-2.5">
        <span className="text-sm font-medium text-kumo-default">
          {error.name ?? "Error"}
        </span>
      </div>
      <div className="relative rounded-t-lg border-t border-kumo-fill bg-kumo-base">
        <pre className="max-h-64 overflow-y-auto px-4 py-3 font-mono text-sm wrap-break-word whitespace-pre-wrap text-kumo-subtle">
          {error.message ?? "Unknown error"}
        </pre>
        <div className="absolute top-1.5 right-1.5">
          <IconCopyButton
            text={`${error.name ?? "Error"}: ${error.message ?? ""}`}
            label="Copy error"
          />
        </div>
      </div>
    </div>
  );
});

const STEP_TYPE_FILTERS = [
  { value: "all", label: "All", icon: ListBulletsIcon },
  { value: "do", label: "Do", icon: CodeIcon },
  { value: "sleep", label: "Sleep", icon: MoonIcon },
  { value: "waitForEvent", label: "Wait for Event", icon: BellIcon }
] as const;

function matchesTypeFilter(step: WorkflowStepRecord, filter: string): boolean {
  if (filter === "all") return true;
  if (filter === "sleep") {
    return step.kind === "sleep" || step.kind === "sleepUntil";
  }
  return step.kind === filter;
}

const StepHistory = memo(function StepHistory({
  steps,
  onRestartFromStep,
  onOpenSource
}: {
  steps: readonly WorkflowStepRecord[];
  onRestartFromStep?: (step: WorkflowStepRecord) => void;
  onOpenSource: (line: number) => void;
}) {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  // Track expanded steps by stable key — persists across polling refreshes
  const [expandedStepKeys, setExpandedStepKeys] = useState<Set<string>>(
    () => new Set()
  );

  const toggleStepExpanded = useCallback((stepKey: string) => {
    setExpandedStepKeys((prev) => {
      const next = new Set(prev);
      if (next.has(stepKey)) {
        next.delete(stepKey);
      } else {
        next.add(stepKey);
      }
      return next;
    });
  }, []);

  const filtered = [...steps].reverse().filter((step) => {
    if (!matchesTypeFilter(step, typeFilter)) {
      return false;
    }
    if (search.trim()) {
      return step.name.toLowerCase().includes(search.trim().toLowerCase());
    }
    return true;
  });

  const activeFilter =
    STEP_TYPE_FILTERS.find((f) => f.value === typeFilter) ??
    STEP_TYPE_FILTERS[0];
  const ActiveIcon = activeFilter.icon;

  return (
    <div>
      <div className="mb-3">
        <h4 className="text-lg font-semibold text-kumo-default">
          Step History
        </h4>
      </div>

      {/* Search + Filter controls */}
      {steps.length > 0 && (
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon
              size={14}
              className="absolute top-1/2 left-3 -translate-y-1/2 text-kumo-subtle"
            />
            <input
              className="focus-visible:ring-kumo-ring h-9 w-full rounded-lg border border-kumo-fill bg-kumo-base pr-3 pl-9 text-sm text-kumo-default placeholder:text-kumo-subtle focus:border-kumo-brand focus:outline-none focus-visible:ring-2"
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search steps..."
              type="text"
              value={search}
            />
          </div>
          <DropdownMenu>
            <DropdownMenu.Trigger
              render={
                <button
                  type="button"
                  className="inline-flex h-9 w-full cursor-pointer items-center justify-between rounded-lg border border-kumo-fill bg-kumo-base px-3 text-sm text-kumo-default transition-colors hover:bg-kumo-fill sm:w-44"
                >
                  <span className="flex items-center gap-2">
                    <ActiveIcon size={14} className="text-kumo-subtle" />
                    {activeFilter.label}
                  </span>
                  <CaretDownIcon size={14} className="text-kumo-subtle" />
                </button>
              }
            />
            <DropdownMenu.Content align="end" className="w-44" side="bottom">
              {STEP_TYPE_FILTERS.map(({ value, label, icon: Icon }) => (
                <DropdownMenu.Item
                  className="flex cursor-pointer items-center gap-2"
                  key={value}
                  onClick={() => setTypeFilter(value)}
                >
                  <Icon size={14} className="text-kumo-subtle" />
                  {label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-kumo-fill bg-kumo-base">
        <div className="min-w-150">
          {steps.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-kumo-subtle">
              No steps recorded yet
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-kumo-subtle">
              No steps match your search
            </div>
          ) : (
            filtered.map((step) => {
              const key = getStepKey(step);
              return (
                <WorkflowStepRow
                  key={key}
                  step={step}
                  isExpanded={expandedStepKeys.has(key)}
                  onToggleExpanded={() => toggleStepExpanded(key)}
                  onRestartFromStep={onRestartFromStep}
                  onOpenSource={onOpenSource}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
});

export function WorkflowInstanceSurface({
  workspaceId,
  definition,
  instanceId,
  active,
  onOpenSource,
  onOpenGraph
}: WorkflowInstanceSurfaceProps) {
  const query = useWorkflowQuery({ view: "instance", workspaceId, active, instanceId, className: definition.className, workerId: definition.routing.workerId, runtimeNamespaceId: definition.routing.runtimeNamespaceId, definitionVersion: definition.updatedAt });
  const mutation = useWorkflowMutation(query.selectionKey, true, workflowControlBusyKey(workspaceId, instanceId));
  const instance = query.snapshot.instance, loaded = query.snapshot.loaded, error = mutation.snapshot.error ?? query.snapshot.error;
  const actionInProgress = mutation.snapshot.operation;
  const clipboard = useWorkflowClipboard(instanceId, 1000), copied = clipboard.copied;
  const [sendEventOpen, setSendEventOpen] = useState(false);
  const [restartIntent, setRestartIntent] = useState<WorkflowRestartIntent | null>(null);
  const restartFromStepTarget = restartIntent?.selectionKey === query.selectionKey ? restartIntent : null;
  const restartingFromStep = mutation.snapshot.pending && restartFromStepTarget !== null;
  const status = instance?.status ?? "unknown", isTerminal = instance ? isTerminalStatus(instance.status) : true;
  const fetchInstance = () => query.dispatch({ _tag: "Refresh" });
  useWorkflowCompletion(mutation, snapshot => { setRestartIntent(null);if (snapshot.result) fetchInstance(); });
  useEffect(() => { if (!active) { setSendEventOpen(false);setRestartIntent(null); } }, [active]);
  useEffect(() => { setSendEventOpen(false);setRestartIntent(null); }, [query.selectionKey]);
  function handleAction(action: WorkflowUiAction) { mutation.dispatch({ _tag: "Control", selectionKey: query.selectionKey, target: { workspaceId, instanceId }, control: { action } }); }
  function handleConfirmRestartFromStep() { if (restartFromStepTarget) mutation.dispatch({ _tag: "Control", selectionKey: restartFromStepTarget.selectionKey, target: restartFromStepTarget.target, control: { action: "restart", from: restartFromStepTarget.from } }); }
  const copyInstanceId = clipboard.copy;

  if (loaded && !instance) {
    return (
      <div className="flex h-full flex-col items-center justify-center space-y-2 bg-kumo-base p-12 text-center text-kumo-subtle">
        <h2 className="text-2xl font-medium">Instance not found</h2>
        <p className="text-sm font-light">
          Instance <code className="font-mono">{instanceId}</code> is no
          longer tracked for {definition.className}.
        </p>
      </div>
    );
  }

  const steps = instance?.steps ?? [];
  const availableActions = instance ? getAvailableActions(instance.status) : [];
  const mutating = mutation.busy || !mutation.ready;

  return (
    <div className="h-full min-h-0 overflow-auto bg-kumo-base">
      {/* Header */}
      <div className="px-6 pt-6">
        <header className="flex flex-wrap items-center justify-between gap-2 pb-6 sm:gap-3">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <h1 className="shrink-0 text-xl font-semibold text-kumo-default">
              {definition.className}
            </h1>
            <Tooltip content={copied ? "Copied!" : "Copy instance ID"} asChild>
              <button
                type="button"
                className={`min-w-0 cursor-pointer truncate font-mono text-base transition-colors ${copied ? "text-kumo-default" : "text-kumo-subtle hover:text-kumo-default"}`}
                onClick={() => void copyInstanceId()}
              >
                #{instanceId}
              </button>
            </Tooltip>
            <WorkflowStatusIcon status={status} />
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-1.5 sm:gap-2">
            {availableActions.map((action) => {
              const Icon = ACTION_ICONS[action];
              const isTerminate = action === "terminate";
              return (
                <button
                  key={action}
                  type="button"
                  className={`inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border px-3 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                    isTerminate
                      ? "border-kumo-fill bg-kumo-base text-kumo-danger hover:bg-kumo-badge-red/10"
                      : "border-kumo-fill bg-kumo-base text-kumo-default hover:bg-kumo-fill"
                  }`}
                  disabled={mutating}
                  aria-busy={!mutation.ready}
                  onClick={() => void handleAction(action)}
                >
                  <Icon
                    size={14}
                    weight={action === "restart" ? "regular" : "fill"}
                    className={actionInProgress === action ? "animate-spin" : ""}
                  />
                  {action.charAt(0).toUpperCase() + action.slice(1)}
                </button>
              );
            })}
            {!isTerminal && (
              <button
                type="button"
                className="inline-flex size-9 cursor-pointer items-center justify-center rounded-lg border border-kumo-fill bg-kumo-base text-kumo-default transition-colors hover:bg-kumo-fill disabled:cursor-not-allowed disabled:opacity-40"
                disabled={mutating}
                  aria-busy={!mutation.ready}
                onClick={() => setSendEventOpen(true)}
                title="Send Event"
              >
                <PaperPlaneTiltIcon size={14} />
              </button>
            )}
            <button
              type="button"
              className="inline-flex size-9 cursor-pointer items-center justify-center rounded-lg border border-kumo-fill bg-kumo-base text-kumo-default transition-colors hover:bg-kumo-fill"
              onClick={onOpenGraph}
              title="Open source graph"
            >
              <FlowArrow size={14} />
            </button>
          </div>
        </header>
      </div>

      {/* Stats strip overlapping the divider line */}
      <div className="relative">
        <hr className="absolute top-1/2 w-full border-kumo-fill" />
        <div className="relative px-6">
          <StatsStrip
            status={status}
            stepCount={steps.length}
            start={instance?.createdAt}
            end={isTerminal ? instance?.updatedAt : undefined}
          />
        </div>
      </div>

      {/* Content */}
      <div className="space-y-6 px-6 py-6">
        {error && (
          <div className="rounded-lg border border-kumo-danger/20 bg-kumo-danger/8 p-4 text-sm text-kumo-danger">
            {error}
          </div>
        )}

        {!loaded ? (
          <div className="p-12 text-center text-kumo-subtle">Loading...</div>
        ) : (
          <>
            <ParamsOutput
              params={instance?.params}
              output={instance?.output}
            />

            {instance?.error && <ErrorCard error={instance.error} />}

            <StepHistory
              steps={steps}
              onRestartFromStep={mutating ? undefined : step => setRestartIntent(workflowRestartIntent(query.selectionKey, { workspaceId, instanceId }, step))}
              onOpenSource={(line) => onOpenSource(definition.file, line)}
            />
          </>
        )}
      </div>

      <WorkflowSendEventDialog
        workspaceId={workspaceId}
        instanceId={instanceId}
        open={sendEventOpen}
        disabled={mutating}
                  aria-busy={!mutation.ready}
        onOpenChange={setSendEventOpen}
        onSent={() => void fetchInstance()}
      />

      {/* Restart from step confirmation dialog */}
      <Dialog.Root
        open={restartFromStepTarget !== null}
        onOpenChange={(open) => {
          if (!open && !restartingFromStep) {
            setRestartIntent(null);
          }
        }}
      >
        <Dialog size="lg" className="w-lg">
          <div className="border-b border-kumo-fill px-6 py-4">
            <Dialog.Title className="text-lg font-semibold text-kumo-default">
              Restart from this step?
            </Dialog.Title>
          </div>

          <div className="px-6 py-5">
            <p className="text-sm text-kumo-subtle">
              This will rerun the instance from{" "}
              <span className="font-medium text-kumo-default">
                {restartFromStepTarget?.from.name}
              </span>
              . Saved state for this step and later steps will be cleared,
              while earlier completed steps are kept.
            </p>
          </div>

          <div className="flex justify-end gap-2 border-t border-kumo-fill px-6 py-4">
            <Button
              variant="secondary"
              onClick={() => setRestartIntent(null)}
              disabled={restartingFromStep}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={restartingFromStep}
              loading={restartingFromStep}
              onClick={() => void handleConfirmRestartFromStep()}
            >
              Restart from step
            </Button>
          </div>
        </Dialog>
      </Dialog.Root>
    </div>
  );
}
